import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { API_BASE } from '../config';
import { api, errorText } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { SkeletonList } from '../components/ui/Skeleton';
import ErrorState from '../components/ui/ErrorState';
import Modal from '../components/ui/Modal';
import {
  FileText,
  Upload,
  Trash2,
  RefreshCw,
  File,
  X,
  CheckCircle,
  AlertCircle,
  Download,
  Eye,
  Sparkles,
  Database,
  FileSignature
} from 'lucide-react';

interface Resume {
  id: number;
  filename: string;
  original_filename: string;
  file_size: number;
  file_type: string;
  document_type?: string;
  keywords: string | null;
  parsed_content?: string;
  created_at: string;
  updated_at: string;
}

const Resumes: React.FC = () => {
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [viewingResume, setViewingResume] = useState<Resume | null>(null);
  const [extracting, setExtracting] = useState<number | null>(null);
  const [reparsing, setReparsing] = useState<number | null>(null);
  const [reindexing, setReindexing] = useState(false);
  const [reindexResult, setReindexResult] = useState<{ sections_created: number; skipped_dupes: number; new_versions: number; sources_processed: number } | null>(null);
  const [uploadingCoverLetter, setUploadingCoverLetter] = useState(false);
  const [coverLetterError, setCoverLetterError] = useState<string | null>(null);
  const [coverLetterSuccess, setCoverLetterSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverLetterInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const fetchResumes = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await api.get<{ resumes?: Resume[] }>('/api/resumes', { retry: 2 });
      setResumes(data.resumes || []);
    } catch (e) {
      setLoadError(errorText(e, 'Failed to load resumes'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchResumes(); }, [fetchResumes]);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'Z');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const fileTypeLabel = (type: string) => {
    if (type === 'application/pdf') return 'PDF';
    if (type.includes('wordprocessingml')) return 'DOCX';
    return 'File';
  };

  const fileTypeBadge = (type: string) => {
    if (type === 'application/pdf') return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
    if (type.includes('wordprocessingml')) return 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300';
    return 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300';
  };

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];

    const allowed = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) {
      setUploadError('Only PDF and DOCX files are allowed.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File too large. Maximum size is 10MB.');
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_BASE}/api/resumes/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();

      if (!res.ok) {
        setUploadError(data.error || 'Upload failed');
        return;
      }

      setUploadSuccess(`"${file.name}" uploaded successfully`);
      await fetchResumes();
      setTimeout(() => setUploadSuccess(null), 4000);
    } catch {
      setUploadError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id: number, name: string) => {
    const ok = await confirm({
      title: 'Delete resume',
      message: `Delete "${name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/resumes/${id}`);
      await fetchResumes();
      toast.success('Resume deleted');
    } catch (e) {
      toast.error(errorText(e, 'Delete failed'));
    }
  };

  const handleViewResume = async (id: number) => {
    try {
      const data = await api.get<Resume>(`/api/resumes/${id}`);
      setViewingResume(data);
    } catch (e) {
      toast.error(errorText(e, 'Failed to load resume details'));
    }
  };

  const handleExtract = async (id: number) => {
    setExtracting(id);
    try {
      await api.post(`/api/resumes/${id}/extract`);
      await fetchResumes();
    } catch (e) {
      toast.error(errorText(e, 'Failed to extract keywords'));
    } finally {
      setExtracting(null);
    }
  };

  const handleReparse = async (id: number) => {
    setReparsing(id);
    try {
      const data = await api.post<{ parsed_content: string }>(`/api/resumes/${id}/reparse`);
      // Refresh the modal view
      if (viewingResume && viewingResume.id === id) {
        setViewingResume({ ...viewingResume, parsed_content: data.parsed_content });
      }
      await fetchResumes();
    } catch (e) {
      toast.error(errorText(e, 'Failed to re-parse resume'));
    } finally {
      setReparsing(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  const handleReindex = async () => {
    setReindexing(true);
    setReindexResult(null);
    try {
      const data = await api.post<{
        sections_created: number;
        skipped_dupes: number;
        new_versions: number;
        sources_processed: number;
      }>('/api/apply-optimizer/reindex-sections');
      setReindexResult(data);
    } catch (e) {
      toast.error(errorText(e, 'Failed to re-index Career Store'));
    } finally {
      setReindexing(false);
    }
  };

  const handleCoverLetterUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];

    const allowed = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) {
      setCoverLetterError('Only PDF and DOCX files are allowed.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setCoverLetterError('File too large. Maximum size is 10MB.');
      return;
    }

    setUploadingCoverLetter(true);
    setCoverLetterError(null);
    setCoverLetterSuccess(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_BASE}/api/resumes/upload-cover-letter`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();

      if (!res.ok) {
        setCoverLetterError(data.error || 'Upload failed');
        return;
      }

      setCoverLetterSuccess(`"${file.name}" uploaded as cover letter`);
      await fetchResumes();
      setTimeout(() => setCoverLetterSuccess(null), 4000);
    } catch {
      setCoverLetterError('Upload failed. Please try again.');
    } finally {
      setUploadingCoverLetter(false);
      if (coverLetterInputRef.current) coverLetterInputRef.current.value = '';
    }
  };

  const handleCoverLetterDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleCoverLetterUpload(e.dataTransfer.files);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <FileText className="w-8 h-8 text-primary-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Resumes</h1>
            <p className="text-gray-600 dark:text-slate-400 mt-1">Upload and manage your resumes for AI-powered analysis</p>
          </div>
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50"
        >
          {uploading ? (
            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
          ) : (
            <><Upload className="w-4 h-4 mr-2" />Upload Resume</>
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx"
          className="hidden"
          onChange={(e) => handleUpload(e.target.files)}
        />
      </div>

      {/* Upload Status Messages */}
      {uploadError && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl"
        >
          <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
          <span className="text-red-700 dark:text-red-300 text-sm">{uploadError}</span>
          <button onClick={() => setUploadError(null)} className="ml-auto text-red-400 hover:text-red-600 dark:hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      )}
      {uploadSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl"
        >
          <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
          <span className="text-green-700 dark:text-green-300 text-sm">{uploadSuccess}</span>
        </motion.div>
      )}

      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
          dragOver
            ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
            : 'border-gray-300 dark:border-slate-700 hover:border-gray-400 dark:hover:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-800'
        }`}
      >
        <Upload className={`w-10 h-10 mx-auto mb-3 ${dragOver ? 'text-primary-500' : 'text-gray-400 dark:text-slate-500'}`} />
        <p className="text-sm font-medium text-gray-700 dark:text-slate-300">
          {dragOver ? 'Drop your resume here' : 'Drag & drop a resume, or click to browse'}
        </p>
        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">PDF or DOCX, up to 10MB</p>
      </div>

      {/* Cover Letter Drop Zone */}
      {coverLetterError && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl"
        >
          <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
          <span className="text-red-700 dark:text-red-300 text-sm">{coverLetterError}</span>
          <button onClick={() => setCoverLetterError(null)} className="ml-auto text-red-400 hover:text-red-600 dark:hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      )}
      {coverLetterSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl"
        >
          <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
          <span className="text-green-700 dark:text-green-300 text-sm">{coverLetterSuccess}</span>
        </motion.div>
      )}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleCoverLetterDrop}
        onClick={() => coverLetterInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
          dragOver
            ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/20'
            : 'border-purple-200 dark:border-purple-800 hover:border-purple-400 dark:hover:border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20'
        }`}
      >
        <FileSignature className={`w-8 h-8 mx-auto mb-2 ${dragOver ? 'text-purple-500' : 'text-purple-400 dark:text-purple-500'}`} />
        <p className="text-sm font-medium text-gray-700 dark:text-slate-300">
          {uploadingCoverLetter ? 'Uploading cover letter...' : 'Drag & drop a cover letter, or click to browse'}
        </p>
        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">PDF or DOCX -- parsed separately for tone and phrasing</p>
      </div>
      <input
        ref={coverLetterInputRef}
        type="file"
        accept=".pdf,.docx"
        className="hidden"
        onChange={(e) => handleCoverLetterUpload(e.target.files)}
      />

      {/* Resumes List */}
      {loading ? (
        <SkeletonList rows={4} />
      ) : loadError ? (
        <ErrorState message={loadError} onRetry={fetchResumes} />
      ) : resumes.length === 0 ? (
        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-12 text-center">
          <FileText className="w-16 h-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No resumes yet</h3>
          <p className="text-gray-500 dark:text-slate-400 mb-4">Upload a resume to get started with AI-powered analysis</p>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700"
          >
            Upload Your First Resume
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-800">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Your Resumes ({resumes.length})</h3>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-slate-800">
            {resumes.map((resume, index) => (
              <motion.div
                key={resume.id}
                className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="flex items-center space-x-4 flex-1 min-w-0">
                  <div className="p-2 bg-gray-100 dark:bg-slate-700 rounded-xl">
                    <File className="w-6 h-6 text-gray-600 dark:text-slate-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {resume.original_filename}
                    </p>
                    <div className="flex items-center space-x-3 mt-1">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${fileTypeBadge(resume.file_type)}`}>
                        {fileTypeLabel(resume.file_type)}
                      </span>
                      {resume.document_type === 'cover_letter' && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
                          Cover Letter
                        </span>
                      )}
                      <span className="text-xs text-gray-500 dark:text-slate-400">{formatFileSize(resume.file_size)}</span>
                      <span className="text-xs text-gray-500 dark:text-slate-400">{formatDate(resume.created_at)}</span>
                      {resume.keywords && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
                          Keywords extracted
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  {!resume.keywords && (
                    <button
                      onClick={() => handleExtract(resume.id)}
                      disabled={extracting === resume.id}
                      className="p-2 text-gray-400 dark:text-slate-500 hover:text-purple-600 rounded-xl hover:bg-purple-50 dark:hover:bg-purple-900/20 disabled:opacity-50"
                      title="Extract keywords with AI"
                    >
                      {extracting === resume.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4" />
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => handleViewResume(resume.id)}
                    className="p-2 text-gray-400 dark:text-slate-500 hover:text-primary-600 rounded-xl hover:bg-primary-50 dark:hover:bg-primary-900/20"
                    title="View details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <a
                    href={`${API_BASE}/api/resumes/${resume.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-gray-400 dark:text-slate-500 hover:text-green-600 rounded-xl hover:bg-green-50 dark:hover:bg-green-900/20"
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => handleDelete(resume.id, resume.original_filename)}
                    className="p-2 text-gray-400 dark:text-slate-500 hover:text-red-600 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Career Store Re-index */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/40 rounded-xl">
              <Database className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Career Store</h3>
              <p className="text-sm text-gray-500 dark:text-slate-400">
                Re-index all documents (resumes, LinkedIn, interviews, cover letters) into a unified searchable store
              </p>
            </div>
          </div>
          <button
            onClick={handleReindex}
            disabled={reindexing}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 whitespace-nowrap"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${reindexing ? 'animate-spin' : ''}`} />
            {reindexing ? 'Re-indexing...' : 'Re-index Career Store'}
          </button>
        </div>
        {reindexResult && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-4 grid grid-cols-4 gap-4"
          >
            <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-xl">
              <p className="text-lg font-bold text-green-700 dark:text-green-300">{reindexResult.sections_created}</p>
              <p className="text-xs text-green-600 dark:text-green-400">Sections Created</p>
            </div>
            <div className="text-center p-3 bg-primary-50 dark:bg-primary-900/20 rounded-xl">
              <p className="text-lg font-bold text-primary-700 dark:text-primary-300">{reindexResult.new_versions}</p>
              <p className="text-xs text-primary-600 dark:text-primary-400">New Versions</p>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl">
              <p className="text-lg font-bold text-gray-700 dark:text-slate-300">{reindexResult.skipped_dupes}</p>
              <p className="text-xs text-gray-600 dark:text-slate-400">Dupes Skipped</p>
            </div>
            <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
              <p className="text-lg font-bold text-purple-700 dark:text-purple-300">{reindexResult.sources_processed}</p>
              <p className="text-xs text-purple-600 dark:text-purple-400">Sources Processed</p>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Resume Detail Modal */}
      <Modal
        isOpen={!!viewingResume}
        onClose={() => setViewingResume(null)}
        title="Resume Details"
        size="lg"
      >
        {viewingResume && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Filename</p>
                  <p className="text-sm text-gray-900 dark:text-white">{viewingResume.original_filename}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Type</p>
                  <p className="text-sm text-gray-900 dark:text-white">{fileTypeLabel(viewingResume.file_type)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Size</p>
                  <p className="text-sm text-gray-900 dark:text-white">{formatFileSize(viewingResume.file_size)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Uploaded</p>
                  <p className="text-sm text-gray-900 dark:text-white">{formatDate(viewingResume.created_at)}</p>
                </div>
              </div>

              {viewingResume.keywords && (
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">Extracted Keywords</p>
                  <div className="flex flex-wrap gap-2">
                    {viewingResume.keywords.split(',').map((kw, i) => (
                      <span key={i} className="px-2 py-1 text-xs bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 rounded-full">
                        {kw.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {viewingResume.parsed_content && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Parsed Content</p>
                    {viewingResume.file_type.includes('wordprocessingml') && (
                      <button
                        onClick={() => handleReparse(viewingResume.id)}
                        disabled={reparsing === viewingResume.id}
                        className="flex items-center px-3 py-1 text-xs font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 rounded-xl hover:bg-primary-100 dark:hover:bg-primary-900/40 disabled:opacity-50"
                      >
                        <RefreshCw className={`w-3 h-3 mr-1 ${reparsing === viewingResume.id ? 'animate-spin' : ''}`} />
                        {reparsing === viewingResume.id ? 'Re-parsing...' : 'Re-parse DOCX'}
                      </button>
                    )}
                  </div>
                  <pre className="text-xs text-gray-700 dark:text-slate-300 bg-gray-50 dark:bg-slate-800/50 p-4 rounded-xl overflow-auto max-h-64 whitespace-pre-wrap font-mono">
                    {viewingResume.parsed_content}
                  </pre>
                </div>
              )}
            </div>
        )}
      </Modal>
    </div>
  );
};

export default Resumes;
